import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Helpers
import { convertObjectIntoQueryParams, formatDateTime, hasPermission } from 'constants/Helpers';
// Selectors
import { getPermissionsData } from 'store/app/AppSelectors';
// urls
import { getProductionJobsPageUrl } from 'constants/AppUrls';

/**
 * Tab strip of the orders home page. Ranked Order List, Overdue Orders, My Tasks and
 * Completed Orders are views of the same list - they stay on /home and only change
 * which endpoint the page reads and how it is filtered. Dropped Orders and the HOF
 * Dashboard are part of the design but have no page yet, so they render disabled
 * instead of dead links.
 */
const OrdersTabs = ({ deliveryDate, activeTab = 'ranked-orders', onTabSelect }) => {
  const navigate = useNavigate();
  const permissions = useSelector((state) => getPermissionsData({ state }));
  // Same check the rest of the app uses to tell a head of florists apart.
  const isHof = hasPermission(permissions, 'florist_orders', 'head_assign');

  const tabs = [
    { key: 'ranked-orders', label: 'Ranked Order List' },
    { key: 'overdue-orders', label: 'Overdue Orders' },
    { key: 'dropped-orders', label: 'Dropped Orders' },
    { key: 'my-tasks', label: 'My Tasks' },
    { key: 'completed-orders', label: 'Completed Orders' },
    {
      key: 'production-jobs',
      label: 'Production Jobs',
      url: getProductionJobsPageUrl(),
      search: convertObjectIntoQueryParams({
        creationDate: formatDateTime(deliveryDate),
        floristId: -1,
      }),
    },
    // Heads of florists only.
    ...(isHof ? [{ key: 'hof-dashboard', label: 'HOF Dashboard' }] : []),
  ];

  const handleTabClick = (tab) => {
    if (tab.disabled || tab.key === activeTab) {
      return;
    }
    // Tabs without a url are views of the list already on screen.
    if (!tab.url) {
      onTabSelect(tab.key);
      return;
    }
    navigate({ pathname: tab.url, search: tab.search ?? '' });
  };

  return (
    <div className="orders-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`orders-tab${tab.key === activeTab ? ' active' : ''}${
            tab.disabled ? ' disabled' : ''
          }${tab.className ? ` ${tab.className}` : ''}`}
          disabled={tab.disabled}
          title={tab.disabled ? 'Not available yet' : undefined}
          onClick={() => handleTabClick(tab)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default OrdersTabs;
